"use strict";
/*
 * Copyright (C) 2017-2020 HERE Europe B.V.
 * Licensed under Apache 2.0, see full license in LICENSE
 * SPDX-License-Identifier: Apache-2.0
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.techniqueDescriptors = exports.AttrScope = void 0;
var AttrScope;
(function (AttrScope) {
    /**
     * Attributes that affect generation of feature geometry and thus must be resolved at decoding
     * time.
     *
     * @remarks
     * They may have huge variancy as they are implemented as vertex attributes or embedded in
     * generated meshes.
     *
     * These attributes are available only in decoding scope.
     */
    AttrScope[AttrScope["FeatureGeometry"] = 0] = "FeatureGeometry";
    /**
     * Attributes that are common to whole group of features drawn with this technique.
     * These attributes affect generated geometry and  thus must be resolved at decoding time.
     *
     * @remarks
     * They shouldn't have big variancy and evaluate to at least dozens of values as each
     * combination of these attributes consitute new technique and material.
     *
     * These attributes are available in decoding and rendering scope.
     */
    AttrScope[AttrScope["TechniqueGeometry"] = 1] = "TechniqueGeometry";
    /**
     * Attributes that are common to whole group of features drawn with this technique.
     *
     * @remarks
     * Attributes that can be changed in resulting object/material from frame to frame. They are
     * usually implemented as uniforms.
     *
     * These attributes may be available only at rendering scope.
     */
    AttrScope[AttrScope["TechniqueRendering"] = 2] = "TechniqueRendering";
})(AttrScope = exports.AttrScope || (exports.AttrScope = {}));
/**
 * @internal
 */
function mergeTechniqueDescriptor(...descriptors) {
    const result = {
        attrScopes: {}
    };
    for (const descriptor of descriptors) {
        if (descriptor.attrTransparencyColor !== undefined) {
            result.attrTransparencyColor = descriptor.attrTransparencyColor;
        }
        if (descriptor.attrScopes !== undefined) {
            result.attrScopes = Object.assign(Object.assign({}, result.attrScopes), descriptor.attrScopes);
        }
    }
    return result;
}
const baseTechniqueParamsDescriptor = {
    // TODO: Choose which techniques should support color with transparency.
    // For now we chosen all, but it maybe not suitable for text or line marker techniques.
    attrTransparencyColor: "color",
    attrScopes: {
        enabled: AttrScope.FeatureGeometry,
        fadeFar: AttrScope.TechniqueRendering,
        fadeNear: AttrScope.TechniqueRendering
    }
};
const pointTechniquePropTypes = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        color: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering
    }
});
const squaresTechniquePropTypes = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, pointTechniquePropTypes);
const circlesTechniquePropTypes = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, pointTechniquePropTypes);
const lineMarkerTechniquePropTypes = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        text: AttrScope.FeatureGeometry,
        label: AttrScope.FeatureGeometry,
        useAbbreviation: AttrScope.FeatureGeometry,
        useIsoCode: AttrScope.FeatureGeometry,
        poiName: AttrScope.FeatureGeometry,
        imageTexture: AttrScope.FeatureGeometry,
        iconColor: AttrScope.TechniqueRendering,
        iconBrightness: AttrScope.TechniqueRendering,
        backgroundColor: AttrScope.TechniqueRendering,
        backgroundSize: AttrScope.TechniqueRendering,
        backgroundOpacity: AttrScope.TechniqueRendering,
        color: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        size: AttrScope.TechniqueRendering
    }
});
const polygonalTechniqueDescriptor = {
    attrScopes: {
        polygonOffset: AttrScope.TechniqueRendering,
        polygonOffsetFactor: AttrScope.TechniqueRendering,
        polygonOffsetUnits: AttrScope.TechniqueRendering,
        lineColor: AttrScope.TechniqueRendering,
        lineFadeFar: AttrScope.TechniqueRendering,
        lineFadeNear: AttrScope.TechniqueRendering
    }
};
const solidLineTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, polygonalTechniqueDescriptor, {
    attrScopes: {
        color: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering,
        lineWidth: AttrScope.TechniqueRendering,
        secondaryWidth: AttrScope.TechniqueRendering,
        secondaryColor: AttrScope.TechniqueRendering,
        dashSize: AttrScope.TechniqueRendering,
        gapSize: AttrScope.TechniqueRendering
    }
});
const lineTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        // TODO, check, which are really dynamic !
        color: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering,
        lineWidth: AttrScope.FeatureGeometry
    }
});
const fillTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, polygonalTechniqueDescriptor, {
    attrScopes: {
        color: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering,
        lineWidth: AttrScope.TechniqueRendering
    }
});
const standardTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        color: AttrScope.FeatureGeometry,
        vertexColors: AttrScope.FeatureGeometry,
        wireframe: AttrScope.TechniqueRendering,
        roughness: AttrScope.TechniqueRendering,
        metalness: AttrScope.TechniqueRendering,
        alphaTest: AttrScope.TechniqueRendering,
        depthTest: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        emissive: AttrScope.TechniqueRendering,
        emissiveIntensity: AttrScope.TechniqueRendering,
        refractionRatio: AttrScope.TechniqueRendering
    }
});
const extrudedPolygonTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, standardTechniqueDescriptor, {
    attrScopes: {
        height: AttrScope.FeatureGeometry,
        floorHeight: AttrScope.FeatureGeometry,
        color: AttrScope.FeatureGeometry,
        defaultColor: AttrScope.FeatureGeometry,
        defaultHeight: AttrScope.FeatureGeometry,
        constantHeight: AttrScope.FeatureGeometry,
        boundaryWalls: AttrScope.FeatureGeometry,
        footprint: AttrScope.FeatureGeometry,
        maxSlope: AttrScope.FeatureGeometry,
        animateExtrusion: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        transparent: AttrScope.TechniqueRendering,
        lineWidth: AttrScope.TechniqueRendering,
        lineFadeNear: AttrScope.TechniqueRendering,
        lineFadeFar: AttrScope.TechniqueRendering,
        lineColor: AttrScope.TechniqueRendering
    }
});
const textTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        text: AttrScope.FeatureGeometry,
        label: AttrScope.FeatureGeometry,
        useAbbreviation: AttrScope.FeatureGeometry,
        useIsoCode: AttrScope.FeatureGeometry,
        backgroundColor: AttrScope.TechniqueRendering,
        backgroundSize: AttrScope.TechniqueRendering,
        backgroundOpacity: AttrScope.TechniqueRendering,
        color: AttrScope.TechniqueRendering,
        opacity: AttrScope.TechniqueRendering,
        priority: AttrScope.TechniqueRendering,
        size: AttrScope.TechniqueRendering
    }
});
const shaderTechniqueDescriptor = mergeTechniqueDescriptor(baseTechniqueParamsDescriptor, {
    attrScopes: {
        params: AttrScope.TechniqueRendering
    }
});
/** @internal  */
exports.techniqueDescriptors = {
    "extruded-polygon": extrudedPolygonTechniqueDescriptor,
    "line-marker": lineMarkerTechniquePropTypes,
    "labeled-icon": lineMarkerTechniquePropTypes,
    "solid-line": solidLineTechniqueDescriptor,
    "dashed-line": solidLineTechniqueDescriptor,
    standard: standardTechniqueDescriptor,
    squares: squaresTechniquePropTypes,
    circles: circlesTechniquePropTypes,
    line: lineTechniqueDescriptor,
    fill: fillTechniqueDescriptor,
    text: textTechniqueDescriptor,
    shader: shaderTechniqueDescriptor
};
//# sourceMappingURL=TechniqueDescriptors.js.map